/**
 * PasswordStrength Component
 *
 * Visual indicator of password strength.
 */

import React, { useMemo } from 'react';

interface PasswordStrengthProps {
  password: string;
}

interface StrengthResult {
  score: number;
  label: string;
  color: string;
  checks: {
    length: boolean;
    lowercase: boolean;
    uppercase: boolean;
    number: boolean;
    special: boolean;
  };
}

export const PasswordStrength: React.FC<PasswordStrengthProps> = ({ password }) => {
  const strength = useMemo((): StrengthResult => {
    const checks = {
      length: password.length >= 8,
      lowercase: /[a-z]/.test(password),
      uppercase: /[A-Z]/.test(password),
      number: /\d/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    };

    const score = Object.values(checks).filter(Boolean).length;

    let label: string;
    let color: string;

    if (score === 0) {
      label = '';
      color = 'bg-gray-200';
    } else if (score <= 2) {
      label = 'Weak';
      color = 'bg-red-500';
    } else if (score <= 3) {
      label = 'Fair';
      color = 'bg-yellow-500';
    } else if (score <= 4) {
      label = 'Good';
      color = 'bg-blue-500';
    } else {
      label = 'Strong';
      color = 'bg-green-500';
    }

    return { score, label, color, checks };
  }, [password]);

  if (!password) return null;

  return (
    <div className="mt-2 space-y-2">
      {/* Strength Bar */}
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((level) => (
          <div
            key={level}
            className={`h-1.5 flex-1 rounded-full transition-colors ${
              strength.score >= level ? strength.color : 'bg-gray-200'
            }`}
          />
        ))}
      </div>

      {/* Strength Label */}
      {strength.label && (
        <p className={`text-xs font-medium ${
          strength.score <= 2 ? 'text-red-600' :
          strength.score <= 3 ? 'text-yellow-600' :
          strength.score <= 4 ? 'text-blue-600' :
          'text-green-600'
        }`}>
          {strength.label}
        </p>
      )}

      {/* Requirements Checklist */}
      <ul className="text-xs text-gray-500 space-y-1">
        <li className={strength.checks.length ? 'text-green-600' : ''}>
          {strength.checks.length ? '✓' : '○'} At least 8 characters
        </li>
        <li className={strength.checks.lowercase && strength.checks.uppercase ? 'text-green-600' : ''}>
          {strength.checks.lowercase && strength.checks.uppercase ? '✓' : '○'} Upper & lowercase letters
        </li>
        <li className={strength.checks.number ? 'text-green-600' : ''}>
          {strength.checks.number ? '✓' : '○'} At least one number
        </li>
        <li className={strength.checks.special ? 'text-green-600' : ''}>
          {strength.checks.special ? '✓' : '○'} Special character (optional)
        </li>
      </ul>
    </div>
  );
};

export default PasswordStrength;
